"use client";

import React from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { User, Mail, Shield, Settings, Info, CheckCircle } from "lucide-react";

export default function ModalFormExample() {
  const [open, setOpen] = React.useState(true);
  const [formData, setFormData] = React.useState({
    firstName: "",
    lastName: "",
    email: "",
    department: "",
    role: "",
    phone: "",
    startDate: "",
    notes: "",
    sendWelcome: true,
    grantAccess: false
  });

  const updateField = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const canSubmit = formData.firstName && formData.lastName && formData.email && formData.role;

  return (
    <div className="space-y-4">
      <Button onClick={() => setOpen(true)}>Open User Creation Form</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-7xl! w-7xl! h-screen bg-transparent shadow-none border-0 p-0 flex items-center justify-center">
          <div className="bg-card/95 backdrop-blur-xl rounded-xl w-full max-h-[90vh] overflow-hidden shadow-2xl border border-white/10 flex flex-col">
            {/* Header */}
            <div className="p-6 pb-4 border-b border-white/10 bg-linear-to-b from-primary/5 to-transparent">
              <h2 className="text-3xl font-bold text-foreground">Create New Team Member</h2>
              <p className="mt-2 text-muted-foreground">Add a new user to your organisation and configure their access permissions</p>
              
              <div className="mt-3 flex items-center gap-4 text-sm">
                <Badge variant="outline">User Management</Badge>
                <Badge variant="secondary">Admin Access Required</Badge>
              </div>
            </div>

            {/* Form Content */}
            <div className="p-6 flex-1 overflow-y-auto space-y-6">
              {/* Personal Information */}
              <Card className="bg-white/5 border-white/20">
                <CardHeader>
                  <CardTitle className="text-xl flex items-center gap-2">
                    <User className="h-5 w-5" />
                    Personal Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">First Name *</Label>
                      <Input 
                        id="firstName" 
                        placeholder="Enter first name"
                        value={formData.firstName}
                        onChange={(e) => updateField("firstName", e.target.value)}
                        className="bg-white/10 border-white/20"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Last Name *</Label>
                      <Input 
                        id="lastName" 
                        placeholder="Enter last name"
                        value={formData.lastName}
                        onChange={(e) => updateField("lastName", e.target.value)}
                        className="bg-white/10 border-white/20"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <Input 
                        id="email" 
                        type="email"
                        placeholder="user@company.com"
                        value={formData.email}
                        onChange={(e) => updateField("email", e.target.value)}
                        className="bg-white/10 border-white/20"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input 
                        id="phone" 
                        placeholder="+1 (555) 123-4567"
                        value={formData.phone}
                        onChange={(e) => updateField("phone", e.target.value)}
                        className="bg-white/10 border-white/20"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Role & Department */}
              <Card className="bg-white/5 border-white/20">
                <CardHeader>
                  <CardTitle className="text-xl flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Role & Department
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="department">Department</Label>
                      <Select value={formData.department} onValueChange={(value) => updateField("department", value)}>
                        <SelectTrigger className="bg-white/10 border-white/20">
                          <SelectValue placeholder="Select department" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="engineering">Engineering</SelectItem>
                          <SelectItem value="product">Product</SelectItem>
                          <SelectItem value="design">Design</SelectItem>
                          <SelectItem value="marketing">Marketing</SelectItem>
                          <SelectItem value="sales">Sales</SelectItem>
                          <SelectItem value="hr">Human Resources</SelectItem>
                          <SelectItem value="finance">Finance</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="role">Role *</Label>
                      <Select value={formData.role} onValueChange={(value) => updateField("role", value)}>
                        <SelectTrigger className="bg-white/10 border-white/20">
                          <SelectValue placeholder="Select role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Administrator</SelectItem>
                          <SelectItem value="manager">Manager</SelectItem>
                          <SelectItem value="lead">Team Lead</SelectItem>
                          <SelectItem value="senior">Senior Member</SelectItem>
                          <SelectItem value="member">Team Member</SelectItem>
                          <SelectItem value="contractor">Contractor</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="startDate">Start Date</Label>
                    <Input 
                      id="startDate" 
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => updateField("startDate", e.target.value)}
                      className="bg-white/10 border-white/20"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Additional Settings */}
              <Card className="bg-white/5 border-white/20">
                <CardHeader>
                  <CardTitle className="text-xl flex items-center gap-2">
                    <Settings className="h-5 w-5" />
                    Additional Settings
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-4">
                    <div className="flex items-start gap-3 p-4 rounded-lg border border-white/10 bg-white/5">
                      <Checkbox 
                        id="sendWelcome" 
                        checked={formData.sendWelcome}
                        onCheckedChange={(checked) => updateField("sendWelcome", Boolean(checked))}
                      />
                      <div className="flex-1">
                        <Label htmlFor="sendWelcome" className="font-medium cursor-pointer">
                          Send Welcome Email
                        </Label>
                        <p className="text-sm text-muted-foreground mt-1">
                          Automatically send a welcome email with login instructions and getting started guide
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3 p-4 rounded-lg border border-white/10 bg-white/5">
                      <Checkbox 
                        id="grantAccess" 
                        checked={formData.grantAccess}
                        onCheckedChange={(checked) => updateField("grantAccess", Boolean(checked))}
                      />
                      <div className="flex-1">
                        <Label htmlFor="grantAccess" className="font-medium cursor-pointer">
                          Grant Immediate Access
                        </Label>
                        <p className="text-sm text-muted-foreground mt-1">
                          Provide immediate access to all team resources and skip approval workflow
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <Separator className="bg-white/10" />
                  
                  <div className="space-y-2">
                    <Label htmlFor="notes">Additional Notes</Label>
                    <Textarea 
                      id="notes"
                      placeholder="Add any additional information about this team member..."
                      value={formData.notes}
                      onChange={(e) => updateField("notes", e.target.value)}
                      className="min-h-[100px] bg-white/10 border-white/20"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Summary */}
              {canSubmit && (
                <Card className="bg-green-500/10 border-green-500/20">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-green-600">
                      <CheckCircle className="h-5 w-5" />
                      <span className="font-medium">Ready to Create User</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {formData.firstName} {formData.lastName} will be added as {formData.role} in the {formData.department || 'selected'} department
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-white/10 px-6 py-4 bg-linear-to-t from-primary/5 to-transparent flex justify-between items-center">
              <div className="text-sm text-muted-foreground">
                {canSubmit ? (
                  <span className="text-green-600">All required fields completed</span>
                ) : (
                  <span>Please fill in all required fields (*)</span>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button disabled={!canSubmit} onClick={() => setOpen(false)}>
                  Create Team Member
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
